import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { logout } from '../utils/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  User, Lock, Bell, Globe, Shield, Eye, HelpCircle, 
  LogOut, Trash2, Moon, Sun, ChevronRight, Loader2, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI, userAPI } from '../services/api';

// Move SettingItem outside the component to prevent re-creation on each render
const SettingItem = ({ icon: Icon, title, description, children }) => (
  <Card className="mb-4">
    <CardContent className="p-4">
      <div className="flex items-start space-x-4">
        <div className="p-2 rounded-lg bg-secondary">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-foreground">{title}</h3>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
          {children && (
            <div className="mt-3">{children}</div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const Settings = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState({
    push: true,
    email: true,
    messages: true,
    trials: true,
    jobs: true,
  });
  const [privacy, setPrivacy] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showLocation: true,
  });
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    // Fetch fresh user data from server to ensure privacy state is accurate
    const fetchPrivacy = async () => {
      try {
        const res = await userAPI.getMyProfile();
        const freshUser = res.data;
        setUser(prev => ({ ...prev, ...freshUser }));
        if (freshUser?.isPrivate) {
          setPrivacy(prev => ({ ...prev, profileVisibility: 'private' }));
        } else {
          setPrivacy(prev => ({ ...prev, profileVisibility: 'public' }));
        }
        // Sync localStorage with fresh data
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        stored.isPrivate = freshUser?.isPrivate || false;
        stored.isMinor = freshUser?.isMinor || false;
        localStorage.setItem('user', JSON.stringify(stored));
      } catch (e) {
        // Fallback to localStorage
        if (userData?.isPrivate) {
          setPrivacy(prev => ({ ...prev, profileVisibility: 'private' }));
        }
      }
    };
    fetchPrivacy();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      // TODO: Implement account deletion API
      toast.error('Account deletion is not yet implemented');
    }
  };

  // Auto-save notification settings
  const handleNotificationChange = (key, checked) => {
    setNotifications(prev => ({ ...prev, [key]: checked }));
    toast.success('Setting updated');
  };

  // Auto-save privacy settings
  const handlePrivacyChange = async (key, value) => {
    setPrivacy(prev => ({ ...prev, [key]: value }));
    if (key === 'profileVisibility') {
      try {
        const isPrivate = value === 'private';
        await userAPI.updatePrivacy(isPrivate);
        // Update localStorage user
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        userData.isPrivate = isPrivate;
        localStorage.setItem('user', JSON.stringify(userData));
        toast.success(isPrivate ? 'Account set to Private' : 'Account set to Public');
      } catch {
        toast.error('Failed to update privacy setting');
      }
    } else {
      toast.success('Setting updated');
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!passwordData.newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await authAPI.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-20">
        <div className="py-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-foreground">Settings</h1>
            <p className="text-muted-foreground">Manage your account and preferences</p>
          </div>

          {/* Account Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Account</h2>
            
            <SettingItem
              icon={User}
              title="Profile Information"
              description="Update your personal details"
            >
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/edit-profile')}
              >
                Edit Profile
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </SettingItem>

            <SettingItem
              icon={Lock}
              title="Change Password"
              description="Update your password regularly for security"
            >
              <div className="space-y-3">
                <div>
                  <Label>Current Password</Label>
                  <Input 
                    type="password" 
                    placeholder="Enter current password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>New Password</Label>
                  <Input 
                    type="password" 
                    placeholder="Enter new password (min 8 characters)"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Confirm New Password</Label>
                  <Input 
                    type="password" 
                    placeholder="Confirm new password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  />
                </div>
                <Button 
                  className="w-full btn-primary text-white" 
                  onClick={handlePasswordChange}
                  disabled={changingPassword}
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </div>
            </SettingItem>
          </div>

          {/* Appearance Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Appearance</h2>
            
            <SettingItem
              icon={theme === 'dark' ? Moon : Sun}
              title="Dark Mode"
              description="Switch between light and dark themes"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">
                  {theme === 'dark' ? 'Dark mode enabled' : 'Light mode enabled'}
                </span>
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                />
              </div>
            </SettingItem>
          </div>

          {/* Notifications Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Notifications</h2>
            
            <SettingItem
              icon={Bell}
              title="Push Notifications"
              description="Receive push notifications on your device"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Push notifications</span>
                  <Switch
                    checked={notifications.push}
                    onCheckedChange={(checked) => handleNotificationChange('push', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Email notifications</span>
                  <Switch
                    checked={notifications.email}
                    onCheckedChange={(checked) => handleNotificationChange('email', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">New messages</span>
                  <Switch
                    checked={notifications.messages}
                    onCheckedChange={(checked) => handleNotificationChange('messages', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Trial updates</span>
                  <Switch
                    checked={notifications.trials}
                    onCheckedChange={(checked) => handleNotificationChange('trials', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Job opportunities</span>
                  <Switch
                    checked={notifications.jobs}
                    onCheckedChange={(checked) => handleNotificationChange('jobs', checked)}
                  />
                </div>
              </div>
            </SettingItem>
          </div>

          {/* Privacy Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Privacy & Security</h2>
            
            {user?.isMinor && (
              <Card className="mb-4 border-amber-500/30">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-4">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                      <ShieldCheck className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">Minor Account Protection</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Your account has enhanced safety features enabled. Your profile is set to Private and only connections can send you direct messages. These settings cannot be changed for your protection.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <SettingItem
              icon={Eye}
              title="Private Account"
              description={user?.isMinor 
                ? "Your account is locked to Private for safety (minor account protection)." 
                : "When private, only approved followers can see your posts, stats, and details"
              }
            >
              <Switch
                checked={privacy.profileVisibility === 'private'}
                onCheckedChange={(checked) => handlePrivacyChange('profileVisibility', checked ? 'private' : 'public')}
                disabled={!!user?.isMinor}
                data-testid="privacy-toggle"
              />
            </SettingItem>

            <SettingItem
              icon={Shield}
              title="Privacy Settings"
              description="Manage what information is visible to others"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Show email address</span>
                  <Switch
                    checked={privacy.showEmail}
                    onCheckedChange={(checked) => handlePrivacyChange('showEmail', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Show location</span>
                  <Switch
                    checked={privacy.showLocation}
                    onCheckedChange={(checked) => handlePrivacyChange('showLocation', checked)}
                  />
                </div>
              </div>
            </SettingItem>
          </div>

          {/* Support Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Support</h2>
            
            <SettingItem
              icon={HelpCircle}
              title="Help & Support"
              description="Get help or report issues"
            >
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => toast.info('Help center coming soon')}
              >
                Visit Help Center
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </SettingItem>

            <SettingItem
              icon={Globe}
              title="About SportiZn"
              description="Version 1.0.0"
            >
              <div className="text-sm text-muted-foreground">
                <p>© 2026 SportiZn. All rights reserved.</p>
                <p className="mt-2">
                  <a href="#" className="text-primary hover:underline">Terms of Service</a> · 
                  <a href="#" className="text-primary hover:underline ml-2">Privacy Policy</a>
                </p>
              </div>
            </SettingItem>
          </div>

          {/* Actions Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Account Actions</h2>
            
            <Button 
              variant="outline" 
              className="w-full mb-3"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>

            <Button 
              variant="destructive" 
              className="w-full"
              onClick={handleDeleteAccount}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
